import React from "react";
import {
  Table,
  Spinner,
  SpaceBetween,
  Header,
  Alert,
  Grid,
  Button,
  CopyToClipboard,
  KeyValuePairs,
  Container,
  ColumnLayout,
  ExpandableSection,
  Link,
} from "@amzn/awsui-components-console";
import "./payload-display.css";
import { configureDomain } from "src/auth/config";
import {
  getTransactionInfo,
  LabelledLink,
} from "src/pages/commons/transaction-info";
interface PayloadProps {
  searchResults: any;
  searchId: string;
  selectedVersion: any;
}

type DataColumnCustomValue = {
  label: string;
  cell: (item: any, parent?: any) => any; // for list-type columns, parent is optional to provide the global-version of the transaction
  copyable?: boolean;
};

type DataColumns = {
  [key: string]: string | DataColumnCustomValue;
};

const COPYABLE_VALUE_FORMATTER = (value: any) => (
  <CopyToClipboard
    copySuccessText="Successfully copied"
    copyErrorText="Failed to copy"
    textToCopy={value?.toString() ?? "-"}
    variant="inline"
  />
);

const LABELLED_LINK_FORMATTER = (
  value: LabelledLink,
  skippable: boolean = false,
) =>
  value ? (
    <Link external href={value.link}>
      {value.label}
    </Link>
  ) : skippable ? undefined : (
    "-"
  );

const TRANSACTION_COLUMNS: DataColumns = {
  externalTransactionId: {
    label: "external id",
    cell: (item: any) =>
      COPYABLE_VALUE_FORMATTER(item["externalTransactionId"]),
  },
  transactionVersion: "transaction version",
  eventTimeStamp: {
    label: "event timestamp",
    cell: (item: any) => new Date(item["eventTimeStamp"]).toISOString(),
  },
  creationDate: {
    label: "creation date",
    cell: (item: any) => new Date(item["creationDate"]).toISOString(),
  },
  resellerId: "reseller id",
  eventType: "event type",
  transactionType: "transaction type",
  billingMode: "billing mode",
  isTest: "is test",
  amortizationTreatment: "amortization treatment",
  intent: "intent",
  intentAction: "intent action",
};

const FX_COLUMNS: DataColumns = {
  localCurrencyCode: "local currency code",
  billingCurrencyCode: "billing currency code",
  exchangeRate: {
    label: "exchange rate",
    cell: (item: any) => {
      const rate = item["fxRateQuote"]?.["exchangeRate"];
      return rate != null ? Number(rate).toFixed(10) : "-";
    },
  },
};

const BALANCE_DATA_COLUMNS: DataColumns = {
  lineNumber: "line number",
  localAmount: "local amount",
  localCurrencyCode: {
    label: "local currency code",
    cell: (item: any, transaction: any) => {
      const topLevelCode = transaction?.["localCurrencyCode"];
      const code = item["localCurrencyCode"] ?? topLevelCode ?? "-";
      if (item["localCurrencyCode"] && topLevelCode && code !== topLevelCode) {
        return <strong>{code}</strong>;
      }
      return code;
    },
  },
  billingAmount: "billing amount",
  billingCurrencyCode: {
    label: "billing currency code",
    cell: (item: any, transaction: any) => {
      const topLevelCode = transaction?.["billingCurrencyCode"];
      const code = item["billingCurrencyCode"] ?? topLevelCode ?? "-";
      if (
        item["billingCurrencyCode"] &&
        topLevelCode &&
        code !== topLevelCode
      ) {
        return <strong>{code}</strong>;
      }
      return code;
    },
  },
  type: "type",
  fuseEventAggregationId: {
    label: "fuse event aggregation id",
    cell: (item: any, transaction: any) => {
      const fuseEventAggregationId = item["fuseEventAggregationId"];

      if (!fuseEventAggregationId) {
        return "-";
      }
      if (!transaction) {
        return fuseEventAggregationId;
      }

      if (transaction["billingMode"] === "FIXED_FEE") {
        // for fixed fee, redirect to FixedFeeConfig page
        return (
          <Link external href={`/getFixedFeeConfig/${fuseEventAggregationId}`}>
            {fuseEventAggregationId}
          </Link>
        );
      }

      return fuseEventAggregationId;
    },
  },
};

const ACCOUNTING_CONTROL_PARAMETER_COLUMNS: DataColumns = {
  term: "term",
  product: "product",
  glLineDescriptionPartnerName: "gl line description partner name",
  fuseClientId: "fuse client id",
  businessCountryCode: "business country code",
  buyingCustomerCountryCode: "buying customer country code",
  refundType: "refund type",
  terminationStrategy: "termination strategy",
  accountingTemplate: "accounting template",
  carveSplitName: "carve split name",
  businessUseCase: "business usecase",
};

const FUSEN_CONTROL_PARAMETER_COLUMNS: DataColumns = {
  fusenEnabled: "fusen enabled",
  fusenEnabledDate: {
    label: "fusen enabled date",
    cell: (item: any) =>
      item["fusenEnabledDate"]
        ? new Date(item["fusenEnabledDate"]).toISOString()
        : "-",
  },
  migrationTargetStage: "migration target stage",
};

const TRANSACTION_DATA_COLUMNS: DataColumns = {
  deferralId: {
    label: "deferral id",
    cell: (item: any) => COPYABLE_VALUE_FORMATTER(item["deferral_id"]),
  },
  productGroup: "product group",
  adjustmentDirection: "adjustment direction",
  eventTime: {
    label: "event time",
    cell: (item: any) => new Date(item["eventTime"]).toISOString(),
  },
  eventStartDate: {
    label: "event start date",
    cell: (item: any) => new Date(item["eventStartDate"]).toISOString(),
  },
  eventEndDate: {
    label: "event end date",
    cell: (item: any) => new Date(item["eventEndDate"]).toISOString(),
  },
  transactionDate: {
    label: "transaction date",
    cell: (item: any) => new Date(item["transactionDate"]).toISOString(),
  },
};

const EXTERNAL_DATA_SOURCE_COLUMNS: DataColumns = {
  fuseSubsPlanId: {
    label: "fuse subs plan id",
    cell: (item: any) =>
      item.subscriptionUpdateMessage?.current?.subscription?.planID ??
      item.fusePlanPeriodDetails?.[0]?.subsPlanId ??
      "-",
  },
  subscriptionId: "subscription id",
  entitlementId: {
    label: "entitlement id",
    cell: (item: any) => COPYABLE_VALUE_FORMATTER(item["entitlementId"]),
  },
  entitlementInternalId: {
    label: "entitlement internal id",
    cell: (item: any) =>
      COPYABLE_VALUE_FORMATTER(item["entitlementInternalId"]),
  },
  operationInternalId: {
    label: "operation internal id",
    cell: (item: any) => COPYABLE_VALUE_FORMATTER(item["operationInternalId"]),
  },
};

const EXTERNAL_LINK_FUSE_BILLING_SERVICE_COLUMNS: DataColumns = {
  billingWfLink: {
    label: "Billing WF",
    cell: (item: any) => LABELLED_LINK_FORMATTER(item.fuse.billingWf, true),
  },
  transactionWfLink: {
    label: "Transaction WF",
    cell: (item: any) => LABELLED_LINK_FORMATTER(item.fuse.transactionWf, true),
  },
  fusenWfLink: {
    label: "FUSEN WF",
    cell: (item: any) => LABELLED_LINK_FORMATTER(item.fuse.fusenWf),
  },
};

const EXTERNAL_LINK_ACCOUNTING_DOWNSTREAM_COLUMNS: DataColumns = {
  acidLink: {
    label: "ACID",
    cell: (item: any) => LABELLED_LINK_FORMATTER(item.acid),
  },
  raceLink: {
    label: "Race",
    cell: (item: any) => LABELLED_LINK_FORMATTER(item.race),
  },
  flashLink: {
    label: "Flash",
    cell: (item: any) => LABELLED_LINK_FORMATTER(item.flash),
  },
};

const EXTERNAL_LINK_BENEFIT_SOURCE_COLUMNS: DataColumns = {
  subsLink: {
    label: "Subs",
    cell: (item: any) => LABELLED_LINK_FORMATTER(item.subs, true),
  },
  entitlementLink: {
    label: "Entitlement",
    cell: (item: any) =>
      LABELLED_LINK_FORMATTER(item.fuse.entitlement?.entitlement, true),
  },
  entitlementOperationLink: {
    label: "Entitlement Operation",
    cell: (item: any) =>
      LABELLED_LINK_FORMATTER(item.fuse.entitlement?.operation, true),
  },
  executionLink: {
    label: "Entitlement Execution",
    cell: (item: any) =>
      LABELLED_LINK_FORMATTER(item.fuse.entitlement?.execution, true),
  },
};

const FUSEN_COLUMNS: DataColumns = {
  eventVersionOverride: "event version override",
  sourceS3Key: "s3 key",
  sourceS3Bucket: "s3 bucket",
  fusenMessageGenerated: "fusen message generated",
  fusenMessageArchived: "fusen message archived",
  fusenClientEventGenerated: "fusen client event generated",
};

const DUMMY_COLUMN_DEFINITION = {
  id: "",
  header: "",
  cell: () => "",
};

export class PayloadDisplay extends React.Component<
  PayloadProps,
  { searchId: string }
> {
  constructor(props: any) {
    super(props);
    this.state = {
      searchId: this.props.searchId,
    };
  }

  render() {
    const { searchResults, selectedVersion } = this.props;
    const domain = configureDomain;
    if (searchResults != "init") {
      if (searchResults == "error") {
        return (
          <div>
            <Grid gridDefinition={[{ colspan: { l: 7, xxs: 10 } }]}>
              <Alert header="Search timed out!" type="error"></Alert>
            </Grid>
          </div>
        );
      }
      if (
        searchResults == "invalid" ||
        !searchResults ||
        !searchResults.fbtsV2Response ||
        !Array.isArray(searchResults.fbtsV2Response) ||
        searchResults.fbtsV2Response.length === 0 ||
        !selectedVersion
      ) {
        return (
          <div>
            <Grid gridDefinition={[{ colspan: { l: 7, xxs: 10 } }]}>
              <Alert header="No results found!" type="error"></Alert>
            </Grid>
          </div>
        );
      }
      const { ftsResponse } = searchResults;
      const fbtsV2Response = selectedVersion;

      const externalDataSource = JSON.parse(
        fbtsV2Response["externalDataSource"],
      );

      const transactionInfo = getTransactionInfo(
        fbtsV2Response["externalTransactionId"],
        fbtsV2Response["transactionType"],
        fbtsV2Response["isTest"],
        domain,
        {
          fusePlanId: externalDataSource["fusePlanId"],
          ...(externalDataSource["entitlementInternalId"]
            ? {
                entitlement: {
                  entitlementInternalId:
                    externalDataSource["entitlementInternalId"],
                  operationInternalId:
                    externalDataSource["operationInternalId"],
                },
              }
            : {}),
        },
      );

      // Top-level information
      const transactionTopLevelContainer = (
        <Container header={<Header>Top-Level Information</Header>}>
          <KeyValuePairs
            columns={4}
            items={Object.entries(TRANSACTION_COLUMNS).map(
              ([column, value]) => ({
                label: typeof value === "string" ? value : value.label,
                value:
                  typeof value === "string"
                    ? fbtsV2Response[column] !== undefined
                      ? fbtsV2Response[column].toString()
                      : "-"
                    : value.cell(fbtsV2Response),
              }),
            )}
          />
        </Container>
      );

      let fusenTrackingContainer;

      if (ftsResponse && ftsResponse.result && ftsResponse.result.length > 0) {
        const fusenColumnDefinitions = [
          DUMMY_COLUMN_DEFINITION,
          ...Object.entries(FUSEN_COLUMNS).map(([column, value]) => ({
            id: column,
            header: typeof value === "string" ? value : value.label,
            cell: (item: any) =>
              typeof value === "string"
                ? item[column] !== undefined
                  ? item[column].toString()
                  : "-"
                : value.cell(item),
            sortingField: column,
          })),
          DUMMY_COLUMN_DEFINITION,
        ];

        fusenTrackingContainer = (
          <Table
            columnDefinitions={fusenColumnDefinitions}
            items={ftsResponse.result}
            sortingDisabled={true}
            wrapLines={false}
            resizableColumns={false}
            header={
              <Header counter={`(${ftsResponse.result.length})`}>
                FUSEN Tracking Details
              </Header>
            }
          />
        );
      }

      // Exchange rate information
      const exchangeRateContainer = (
        <Container header={<Header>Exchange Rate Information</Header>}>
          <KeyValuePairs
            columns={4}
            items={Object.entries(FX_COLUMNS).map(([column, value]) => ({
              label: typeof value === "string" ? value : value.label,
              value:
                typeof value === "string"
                  ? fbtsV2Response[column] !== undefined
                    ? fbtsV2Response[column].toString()
                    : "-"
                  : value.cell(fbtsV2Response),
            }))}
          />
        </Container>
      );

      // Balance items
      const balanceDataColumnDefinitions: any[] = [
        DUMMY_COLUMN_DEFINITION,
        ...Object.entries(BALANCE_DATA_COLUMNS).map(([column, value]) => ({
          id: column,
          header: typeof value === "string" ? value : value.label,
          cell: (item: any) =>
            typeof value === "string"
              ? item[column] !== undefined
                ? item[column].toString()
                : "-"
              : value.cell(item, fbtsV2Response),
        })),
        DUMMY_COLUMN_DEFINITION,
      ];
      const balanceDataContainer = (
        <Container
          header={
            <Header counter={`(${fbtsV2Response["balanceItems"].length})`}>
              Balance Items
            </Header>
          }
        >
          <Table
            columnDefinitions={balanceDataColumnDefinitions}
            items={fbtsV2Response["balanceItems"].toSorted(
              (item1: any, item2: any) =>
                item1["lineNumber"] - item2["lineNumber"],
            )}
            sortingDisabled={true}
            wrapLines={false}
            resizableColumns={false}
            variant="borderless"
          ></Table>
        </Container>
      );

      // control parameters (accounting + fusen)
      const controlParameters = fbtsV2Response["controlParameters"];
      const accountingControlParameters = controlParameters["accounting"];
      const fusenControlParameters = controlParameters["fusen"];

      const controlParameterContainer = (
        <Container header={<Header>Control Parameters</Header>}>
          <ColumnLayout borders="horizontal">
            <div>
              <Header variant="h3">Accounting</Header>
              <KeyValuePairs
                columns={4}
                items={Object.entries(ACCOUNTING_CONTROL_PARAMETER_COLUMNS).map(
                  ([column, value]) => ({
                    label: typeof value === "string" ? value : value.label,
                    value:
                      typeof value === "string"
                        ? accountingControlParameters[column] !== undefined
                          ? accountingControlParameters[column].toString()
                          : "-"
                        : value.cell(accountingControlParameters),
                  }),
                )}
              />
            </div>
            <div>
              <Header variant="h3">Fusen</Header>
              <KeyValuePairs
                columns={4}
                items={Object.entries(FUSEN_CONTROL_PARAMETER_COLUMNS).map(
                  ([column, value]) => ({
                    label: typeof value === "string" ? value : value.label,
                    value:
                      typeof value === "string"
                        ? fusenControlParameters[column] !== undefined
                          ? fusenControlParameters[column].toString()
                          : "-"
                        : value.cell(fusenControlParameters),
                  }),
                )}
              />
            </div>
          </ColumnLayout>
        </Container>
      );

      // transaction data
      const transactionData = fbtsV2Response["transactionData"];

      const transactionDataContainer = (
        <Container header={<Header>Transaction Data</Header>}>
          <KeyValuePairs
            columns={4}
            items={Object.entries(TRANSACTION_DATA_COLUMNS).map(
              ([column, value]) => {
                return {
                  label: typeof value === "string" ? value : value.label,
                  value:
                    typeof value === "string"
                      ? transactionData[column] !== undefined
                        ? transactionData[column].toString()
                        : "-"
                      : value.cell(transactionData),
                };
              },
            )}
          />
        </Container>
      );

      // external data source
      const externalDataSourceContainer = (
        <Container header={<Header>External Data Source</Header>}>
          <ColumnLayout borders="horizontal">
            <KeyValuePairs
              columns={4}
              items={[
                {
                  label: "fuse plan id",
                  value: transactionInfo.fuse.fusePlan ? (
                    <div>
                      <Link
                        external
                        href={transactionInfo.fuse.fusePlan.fenderLink}
                      >
                        {externalDataSource["fusePlanId"]}
                      </Link>
                      <Link
                        external
                        href={transactionInfo.fuse.fusePlan.bartLink}
                      >
                        <Button>BART</Button>
                      </Link>
                    </div>
                  ) : (
                    "-"
                  ),
                },
                ...Object.entries(EXTERNAL_DATA_SOURCE_COLUMNS).map(
                  ([column, value]) => ({
                    label: typeof value === "string" ? value : value.label,
                    value:
                      typeof value === "string"
                        ? externalDataSource[column] !== undefined
                          ? externalDataSource[column].toString()
                          : "-"
                        : value.cell(externalDataSource),
                  }),
                ),
              ]}
            />
            <ExpandableSection headerText="Expand Full JSON">
              <pre className="sourceInfo">
                {JSON.stringify(externalDataSource, null, 2)}
              </pre>
            </ExpandableSection>
          </ColumnLayout>
        </Container>
      );

      const getExternalLinkGroupItems = (dataColumns: DataColumns) => {
        const items = Object.entries(dataColumns)
          .map(([_, value]) => {
            const valueCustomValue = value as DataColumnCustomValue;
            return {
              label: valueCustomValue.label,
              value: valueCustomValue.cell(transactionInfo),
              key: valueCustomValue.label,
            };
          })
          .filter((item) => item?.value !== undefined);

        return items.length > 0
          ? items
          : [
              {
                label: "N/A",
                value: "-",
                key: "N/A",
              },
            ];
      };

      const externalLinkContainer = (
        <Container header={<Header>External Links</Header>}>
          <KeyValuePairs
            columns={4}
            items={[
              {
                type: "group",
                title: "FuseBillingService",
                items: getExternalLinkGroupItems(
                  EXTERNAL_LINK_FUSE_BILLING_SERVICE_COLUMNS,
                ),
              },
              {
                type: "group",
                title: "Accounting Downstream",
                items: getExternalLinkGroupItems(
                  EXTERNAL_LINK_ACCOUNTING_DOWNSTREAM_COLUMNS,
                ),
              },
              {
                type: "group",
                title: "Benefit Source",
                items: getExternalLinkGroupItems(
                  EXTERNAL_LINK_BENEFIT_SOURCE_COLUMNS,
                ),
              },
            ]}
          />
        </Container>
      );

      return (
        <div style={{ paddingLeft: "10px" }}>
          <SpaceBetween size="l">
            {transactionTopLevelContainer}
            {fusenTrackingContainer}
            {exchangeRateContainer}
            {balanceDataContainer}
            {controlParameterContainer}
            {transactionDataContainer}
            {externalDataSourceContainer}
            {externalLinkContainer}
          </SpaceBetween>
        </div>
      );
    } else {
      return <Spinner size="large"></Spinner>;
    }
  }
}

export default PayloadDisplay;
